import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import useIsMobile from '../hooks/useIsMobile';

/**
 * Tag multi-select: selected regions as removable chips *inside* the search field.
 * Optional create when a typed name is missing. No nested <form> (parent dialog has one).
 */
export default function RegionCheckboxPicker({
  label,
  hint,
  regions = [],
  selectedIds = [],
  onChange,
  disabled = false,
  emptyMessage = 'لا توجد مناطق لهذه المدينة',
  disabledMessage = 'اختر المدينة أولاً',
  allowCreate = false,
  onCreate,
  createPlaceholder = 'ابحث أو اكتب منطقة…',
}) {
  const isMobile = useIsMobile();
  const inputRef = useRef(null);
  const wrapRef = useRef(null);
  const menuRef = useRef(null);
  const [search, setSearch] = useState('');
  const [open, setOpen] = useState(false);
  const [focused, setFocused] = useState(false);
  const [creating, setCreating] = useState(false);
  const [createError, setCreateError] = useState('');
  const [menuPosition, setMenuPosition] = useState(null);
  /** Keep labels even if regions list lags behind selection. */
  const [labelCache, setLabelCache] = useState({});

  const selectedSet = useMemo(
    () => new Set((selectedIds || []).map((id) => String(id))),
    [selectedIds],
  );

  useEffect(() => {
    if (!regions.length) return;
    setLabelCache((prev) => {
      let changed = false;
      const next = { ...prev };
      for (const r of regions) {
        const key = String(r.id);
        const name = r.name_ar || r.name || '';
        if (name && next[key] !== name) {
          next[key] = name;
          changed = true;
        }
      }
      return changed ? next : prev;
    });
  }, [regions]);

  const selectedChips = useMemo(() => {
    const byId = new Map(regions.map((r) => [String(r.id), r]));
    return (selectedIds || []).map((id) => {
      const sid = String(id);
      const row = byId.get(sid);
      return {
        id: sid,
        name: row?.name_ar || row?.name || labelCache[sid] || `#${sid}`,
      };
    });
  }, [regions, selectedIds, labelCache]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return regions;
    return regions.filter((r) => (r.name_ar || r.name || '').toLowerCase().includes(q));
  }, [regions, search]);

  const canCreate = allowCreate && typeof onCreate === 'function' && !disabled;
  const exactMatch = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return false;
    return regions.some((r) => (r.name_ar || r.name || '').toLowerCase() === q);
  }, [regions, search]);
  const canAddFromSearch = canCreate && search.trim() && !exactMatch;

  useEffect(() => {
    if (disabled) {
      setOpen(false);
      setFocused(false);
      setSearch('');
      setCreateError('');
    }
  }, [disabled]);

  const closeMenu = useCallback(({ clearSearch = true } = {}) => {
    setOpen(false);
    setFocused(false);
    setMenuPosition(null);
    if (clearSearch) setSearch('');
    inputRef.current?.blur();
  }, []);

  useEffect(() => {
    if (!open || disabled) return undefined;

    function onDocPointer(e) {
      const root = wrapRef.current;
      const menu = menuRef.current;
      if (!root) return;
      const path = typeof e.composedPath === 'function' ? e.composedPath() : [];
      const inside = root.contains(e.target)
        || menu?.contains(e.target)
        || path.includes(root)
        || (menu && path.includes(menu));
      if (!inside) closeMenu();
    }

    document.addEventListener('pointerdown', onDocPointer, true);
    return () => document.removeEventListener('pointerdown', onDocPointer, true);
  }, [open, disabled, closeMenu]);

  const listMax = isMobile ? 200 : 240;
  const toolbarHeight = 44;
  const menuChrome = toolbarHeight;

  useLayoutEffect(() => {
    if (!open || disabled) {
      setMenuPosition(null);
      return undefined;
    }

    function updatePosition() {
      const anchor = wrapRef.current;
      if (!anchor) return;
      const rect = anchor.getBoundingClientRect();
      const viewportPadding = 8;
      const width = Math.min(rect.width, window.innerWidth - viewportPadding * 2);
      const left = Math.min(
        Math.max(viewportPadding, rect.left),
        window.innerWidth - width - viewportPadding,
      );
      const estimatedHeight = menuChrome + listMax + 12;
      const spaceBelow = window.innerHeight - rect.bottom - viewportPadding;
      const spaceAbove = rect.top - viewportPadding;
      const openUp = spaceBelow < estimatedHeight && spaceAbove > spaceBelow;
      const top = openUp
        ? Math.max(viewportPadding, rect.top - estimatedHeight - 4)
        : rect.bottom + 4;

      setMenuPosition({ top, left, width, maxHeight: estimatedHeight });
    }

    updatePosition();
    window.addEventListener('resize', updatePosition);
    window.addEventListener('scroll', updatePosition, true);
    return () => {
      window.removeEventListener('resize', updatePosition);
      window.removeEventListener('scroll', updatePosition, true);
    };
  }, [open, disabled, listMax, menuChrome, filtered.length, regions.length, selectedIds.length]);

  function setSelected(ids) {
    onChange(ids.map(String));
  }

  function rememberLabel(id, name) {
    if (id == null || !name) return;
    setLabelCache((prev) => {
      const key = String(id);
      if (prev[key] === name) return prev;
      return { ...prev, [key]: name };
    });
  }

  function addId(id, name) {
    const sid = String(id);
    if (name) rememberLabel(sid, name);
    if (selectedSet.has(sid)) return;
    setSelected([...selectedIds.map(String), sid]);
    setSearch('');
    setCreateError('');
  }

  function removeId(id) {
    const sid = String(id);
    setSelected(selectedIds.map(String).filter((x) => x !== sid));
  }

  function toggle(region) {
    const sid = String(region.id);
    if (selectedSet.has(sid)) removeId(sid);
    else addId(sid, region.name_ar || region.name);
  }

  function clearAll() {
    setSelected([]);
    setSearch('');
    inputRef.current?.focus();
  }

  async function createRegion() {
    const name = search.trim();
    if (!name || !onCreate || creating) return;
    setCreating(true);
    setCreateError('');
    try {
      const region = await onCreate(name);
      if (region?.id != null) {
        addId(region.id, region.name_ar || region.name || name);
        setOpen(true);
        inputRef.current?.focus();
      } else {
        setCreateError('لم يُرجع الخادم المنطقة الجديدة');
      }
    } catch (err) {
      setCreateError(err?.message || 'فشل إضافة المنطقة');
    } finally {
      setCreating(false);
    }
  }

  function handleInputKeyDown(e) {
    if (e.key === 'Enter') {
      e.preventDefault();
      e.stopPropagation();
      if (canAddFromSearch) {
        createRegion();
        return;
      }
      const first = filtered.find((r) => !selectedSet.has(String(r.id))) || filtered[0];
      if (first) toggle(first);
      return;
    }
    if (e.key === 'Backspace' && !search && selectedChips.length > 0) {
      e.preventDefault();
      removeId(selectedChips[selectedChips.length - 1].id);
      return;
    }
    if (e.key === 'Escape') {
      e.preventDefault();
      closeMenu();
      return;
    }
  }

  function focusField() {
    if (disabled) return;
    setFocused(true);
    setOpen(true);
    inputRef.current?.focus();
  }

  function toggleMenu(e) {
    e.preventDefault();
    e.stopPropagation();
    if (open) closeMenu({ clearSearch: false });
    else focusField();
  }

  function confirmSelection(e) {
    e?.preventDefault?.();
    e?.stopPropagation?.();
    closeMenu();
  }

  const hasSelection = selectedChips.length > 0;
  const showMenu = !disabled && open && menuPosition;

  const dropdownMenu = showMenu ? (
    <div
      ref={menuRef}
      style={{
        ...styles.dropdown,
        position: 'fixed',
        top: menuPosition.top,
        left: menuPosition.left,
        width: menuPosition.width,
        maxHeight: menuPosition.maxHeight,
      }}
      role="listbox"
    >
      <div style={styles.dropdownToolbar}>
        <span style={styles.dropdownHint}>
          {regions.length > 0
            ? (search.trim() ? `نتائج البحث (${filtered.length})` : `كل العناصر (${regions.length})`)
            : 'اختر من القائمة'}
        </span>
        <div style={styles.toolbarActions}>
          {hasSelection && (
            <button
              type="button"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                clearAll();
              }}
              style={styles.linkBtn}
            >
              إلغاء التحديد
            </button>
          )}
          <button
            type="button"
            onClick={confirmSelection}
            style={styles.doneBtn}
          >
            تم
          </button>
        </div>
      </div>

      <div style={{ ...styles.list, maxHeight: listMax }}>
        {regions.length === 0 && !canAddFromSearch && (
          <p style={styles.noMatch}>
            {canCreate
              ? 'لا مناطق بعد — اكتب الاسم في الحقل أعلاه ثم Enter'
              : emptyMessage}
          </p>
        )}

        {regions.length > 0 && filtered.length === 0 && !canAddFromSearch && (
          <p style={styles.noMatch}>لا توجد نتائج</p>
        )}

        {filtered.map((region) => {
          const checked = selectedSet.has(String(region.id));
          return (
            <button
              key={region.id}
              type="button"
              role="option"
              aria-selected={checked}
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                toggle(region);
                inputRef.current?.focus();
              }}
              style={{
                ...styles.option,
                backgroundColor: checked ? '#EDE7F6' : 'transparent',
              }}
            >
              <span
                style={{
                  ...styles.checkMark,
                  backgroundColor: checked ? '#3E2C6E' : '#fff',
                  borderColor: checked ? '#3E2C6E' : '#ccc',
                  color: checked ? '#fff' : 'transparent',
                }}
              >
                ✓
              </span>
              <span style={styles.optionLabel}>{region.name_ar || region.name}</span>
            </button>
          );
        })}

        {canAddFromSearch && (
          <button
            type="button"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              createRegion();
            }}
            disabled={creating}
            style={{
              ...styles.createOption,
              opacity: creating ? 0.65 : 1,
            }}
          >
            <span style={styles.createPlus}>+</span>
            <span style={styles.createText}>
              {creating ? 'جاري الإضافة…' : (
                <>إضافة «{search.trim()}» إلى القائمة</>
              )}
            </span>
          </button>
        )}
      </div>
    </div>
  ) : null;

  return (
    <div style={styles.wrap}>
      <div style={styles.header}>
        <label style={styles.label}>{label}</label>
        {hasSelection && (
          <span style={styles.countBadge}>{selectedChips.length} محدد</span>
        )}
      </div>
      {hint && <p style={styles.hint}>{hint}</p>}

      {disabled ? (
        <div style={styles.emptyBox}>{disabledMessage}</div>
      ) : (
        <div style={styles.fieldShell} ref={wrapRef}>
          {/* Search box WITH chips inside */}
          <div
            style={{
              ...styles.control,
              borderColor: focused ? '#3E2C6E' : hasSelection ? '#C5B4E3' : '#d0d0d0',
              backgroundColor: hasSelection ? '#FBF9FE' : '#fff',
              boxShadow: focused ? '0 0 0 3px rgba(62, 44, 110, 0.14)' : 'none',
            }}
            onClick={focusField}
          >
            <div style={styles.controlInner}>
              {selectedChips.map((chip) => (
                <span key={chip.id} style={styles.chip}>
                  <span style={styles.chipText}>{chip.name}</span>
                  <button
                    type="button"
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      removeId(chip.id);
                      inputRef.current?.focus();
                    }}
                    style={styles.chipRemove}
                    aria-label={`إزالة ${chip.name}`}
                  >
                    ×
                  </button>
                </span>
              ))}
              <input
                ref={inputRef}
                type="text"
                value={search}
                onChange={(e) => {
                  setSearch(e.target.value);
                  setOpen(true);
                  setCreateError('');
                }}
                onFocus={() => {
                  setFocused(true);
                  setOpen(true);
                }}
                onKeyDown={handleInputKeyDown}
                placeholder={
                  hasSelection
                    ? 'أضف منطقة أخرى…'
                    : regions.length
                      ? createPlaceholder
                      : canCreate
                        ? 'اكتب اسم المنطقة ثم Enter'
                        : emptyMessage
                }
                style={{
                  ...styles.inlineInput,
                  minWidth: hasSelection ? 90 : 140,
                }}
                dir="rtl"
                disabled={creating}
                autoComplete="off"
              />
            </div>
            <div style={styles.controlActions}>
              {hasSelection && (
                <button
                  type="button"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    clearAll();
                  }}
                  style={styles.clearBtn}
                >
                  مسح
                </button>
              )}
              <button
                type="button"
                onClick={toggleMenu}
                style={styles.chevronBtn}
                aria-label={open ? 'إغلاق القائمة' : 'فتح القائمة'}
                aria-expanded={open}
              >
                <span style={styles.chevron} aria-hidden>{open ? '▴' : '▾'}</span>
              </button>
            </div>
          </div>

          {createError && <p style={styles.createError}>{createError}</p>}
        </div>
      )}
      {typeof document !== 'undefined' && dropdownMenu
        ? createPortal(dropdownMenu, document.body)
        : null}
    </div>
  );
}

const styles = {
  wrap: {
    display: 'flex',
    flexDirection: 'column',
    gap: 6,
    width: '100%',
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 8,
  },
  label: {
    fontSize: 'var(--font-size-base)',
    color: 'var(--color-text)',
    fontWeight: 600,
    margin: 0,
  },
  countBadge: {
    fontSize: 11,
    fontWeight: 700,
    color: '#3E2C6E',
    backgroundColor: '#EDE7F6',
    padding: '3px 8px',
    borderRadius: 999,
  },
  hint: { fontSize: 12, color: '#888', margin: 0, lineHeight: 1.45 },
  emptyBox: {
    padding: 16,
    textAlign: 'center',
    fontSize: 13,
    color: '#999',
    backgroundColor: '#fafafa',
    borderRadius: 10,
    border: '1px dashed #ddd',
  },
  fieldShell: {
    position: 'relative',
    width: '100%',
  },
  control: {
    display: 'flex',
    alignItems: 'center',
    gap: 6,
    minHeight: 48,
    padding: '6px 8px 6px 10px',
    border: '1.5px solid #d0d0d0',
    borderRadius: 12,
    cursor: 'text',
    transition: 'border-color 0.15s, box-shadow 0.15s, background-color 0.15s',
    boxSizing: 'border-box',
    width: '100%',
  },
  controlInner: {
    flex: 1,
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'center',
    gap: 6,
    minWidth: 0,
    minHeight: 34,
  },
  controlActions: {
    display: 'flex',
    alignItems: 'center',
    gap: 4,
    flexShrink: 0,
    alignSelf: 'flex-start',
    marginTop: 6,
  },
  chip: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 2,
    maxWidth: '100%',
    padding: '5px 4px 5px 10px',
    borderRadius: 999,
    fontSize: 12,
    fontWeight: 700,
    backgroundColor: '#3E2C6E',
    color: '#fff',
    lineHeight: 1.25,
    boxShadow: '0 1px 2px rgba(62, 44, 110, 0.25)',
  },
  chipText: {
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    maxWidth: 150,
  },
  chipRemove: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: 22,
    height: 22,
    borderRadius: '50%',
    background: 'rgba(255,255,255,0.2)',
    border: 'none',
    color: '#fff',
    cursor: 'pointer',
    fontSize: 16,
    lineHeight: 1,
    padding: 0,
    flexShrink: 0,
  },
  inlineInput: {
    flex: '1 1 80px',
    border: 'none',
    outline: 'none',
    background: 'transparent',
    fontSize: 14,
    padding: '6px 2px',
    color: '#222',
    boxSizing: 'border-box',
    fontFamily: 'inherit',
  },
  clearBtn: {
    background: 'none',
    border: 'none',
    color: '#888',
    fontSize: 12,
    fontWeight: 600,
    cursor: 'pointer',
    padding: '2px 4px',
    fontFamily: 'inherit',
  },
  chevron: {
    color: '#666',
    fontSize: 12,
    lineHeight: 1,
    userSelect: 'none',
  },
  chevronBtn: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: 28,
    height: 28,
    border: 'none',
    borderRadius: 8,
    background: 'transparent',
    cursor: 'pointer',
    padding: 0,
    flexShrink: 0,
  },
  dropdownToolbar: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 8,
    padding: '8px 12px',
    borderBottom: '1px solid #f0eef5',
    background: '#faf9fc',
    flexShrink: 0,
  },
  toolbarActions: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    flexShrink: 0,
  },
  dropdownHint: { fontSize: 11, color: '#888', fontWeight: 600 },
  linkBtn: {
    background: 'none',
    border: 'none',
    color: '#3E2C6E',
    fontWeight: 600,
    cursor: 'pointer',
    padding: '4px 2px',
    fontSize: 12,
    fontFamily: 'inherit',
    whiteSpace: 'nowrap',
  },
  doneBtn: {
    minWidth: 64,
    padding: '7px 14px',
    borderRadius: 10,
    border: 'none',
    backgroundColor: '#3E2C6E',
    color: '#fff',
    fontWeight: 700,
    fontSize: 13,
    cursor: 'pointer',
    fontFamily: 'inherit',
    whiteSpace: 'nowrap',
  },
  dropdown: {
    zIndex: 1300,
    border: '1px solid #e0d8ef',
    borderRadius: 12,
    backgroundColor: '#fff',
    boxShadow: '0 10px 28px rgba(40, 30, 70, 0.14)',
    overflow: 'hidden',
    display: 'flex',
    flexDirection: 'column',
  },
  list: {
    overflowY: 'auto',
    display: 'flex',
    flexDirection: 'column',
    gap: 2,
    padding: 6,
  },
  option: {
    display: 'flex',
    alignItems: 'center',
    gap: 10,
    width: '100%',
    textAlign: 'right',
    padding: '10px 12px',
    borderRadius: 8,
    border: 'none',
    cursor: 'pointer',
    background: 'transparent',
    fontFamily: 'inherit',
  },
  checkMark: {
    width: 18,
    height: 18,
    borderRadius: 5,
    border: '1.5px solid #ccc',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: 11,
    fontWeight: 700,
    flexShrink: 0,
    boxSizing: 'border-box',
  },
  optionLabel: { fontSize: 14, color: '#333', flex: 1, minWidth: 0 },
  noMatch: {
    textAlign: 'center',
    color: '#999',
    fontSize: 13,
    margin: '14px 8px',
    lineHeight: 1.5,
  },
  createOption: {
    display: 'flex',
    alignItems: 'center',
    gap: 10,
    width: '100%',
    textAlign: 'right',
    padding: '12px',
    borderRadius: 8,
    border: '1px dashed #C5B4E3',
    background: '#F8F5FC',
    color: '#3E2C6E',
    cursor: 'pointer',
    fontFamily: 'inherit',
    fontSize: 13,
    marginTop: 2,
  },
  createPlus: {
    width: 22,
    height: 22,
    borderRadius: '50%',
    background: '#3E2C6E',
    color: '#fff',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontWeight: 700,
    fontSize: 16,
    flexShrink: 0,
    lineHeight: 1,
  },
  createText: { flex: 1, fontWeight: 600 },
  createError: { margin: '6px 2px 0', fontSize: 12, color: '#c62828' },
};
